import React, { useEffect, useState } from "react";
import { Content, ContentGroup, OfferWindow, OfferWindowFormValues } from "../../../../../models/vod/Models";
import {
    useUpsertContentGroupOfferWindows,
    useUpsertContentOfferWindows,
    useGetContentMatchingContentGroupId,
    useGetVodContentIngestorProperties,
} from "../../../../../services/vod/Query";
import EditForm from "./EditForm";
import Modal from "../../../../../context/modal/Modal";
import OfferWindowUpsertForm from "./OfferWindowUpsertForm";
import { toDateTimeString, toIsoDate } from "../../../../../utils/DateUtil";

interface Props {
    content?: Content;
    contentGroup?: ContentGroup;
    openModal: boolean;
    onClose: () => void
}

const OfferWindowsForm = ({ content, contentGroup, openModal, onClose }: Props) => {
    const [ contentState, setContentState ] = useState(content);
    const [ contentGroupId, setContentGroupId ] = useState("");
    const getContentMatchingContentGroupId = useGetContentMatchingContentGroupId(contentGroupId);
    const upsertContentOfferWindows = useUpsertContentOfferWindows();
    const upsertContentGroupOfferWindows = useUpsertContentGroupOfferWindows();
    const { data: ingestorProperties } = useGetVodContentIngestorProperties();

    const formatOfferWindowDates = (offerWindow: OfferWindow) => {
        return {
            ...offerWindow,
            startDateTime: toDateTimeString(offerWindow.startDateTime),
            endDateTime:  toDateTimeString(offerWindow.endDateTime),
        };
    };

    useEffect(() => {
        if (content){
            setContentState({
                ...content,
                offerWindows: content.offerWindows?.map(offerWindow => (formatOfferWindowDates(offerWindow))),
            });
            return;
        }

        if(!content && !contentGroup)
            return;

        if (contentGroup)
            setContentGroupId(contentGroup.id as string);

        if (getContentMatchingContentGroupId.isSuccess){
            const contentFromContentGroup = getContentMatchingContentGroupId.data;
            setContentState({
                ...contentFromContentGroup,
                offerWindows: contentFromContentGroup?.offerWindows?.map(offerWindow => (formatOfferWindowDates(offerWindow))),
            });
        }
    }, [
        content,
        contentGroup,
        getContentMatchingContentGroupId.data,
        getContentMatchingContentGroupId.isSuccess,
    ]);

    if (!ingestorProperties)
        return <></>;

    const modalId = `${contentState?.id}-offer-windows`;
    const contentOfferWindows = contentState?.offerWindows;

    const submit = async (data: OfferWindowFormValues) => {
        const offerWindows = data?.offerWindows
            .map((offerWindow) => {
                const original =
                    Object.prototype.hasOwnProperty.call(offerWindow, "ingestorId") ? ingestorProperties[offerWindow.ingestorId as string] :
                    Object.values(ingestorProperties).find((ingestorOfferWindow) => {
                    return (ingestorOfferWindow.depCode ?? "") === offerWindow.depCode
                        && ingestorOfferWindow.tenantId === offerWindow.tenantId
                        && ingestorOfferWindow.offerType === offerWindow.offerType
                        && JSON.stringify(ingestorOfferWindow.catalogues?.map(c => c.id))
                        === JSON.stringify(offerWindow.catalogues?.map(c => c.id));
                });

                return ({
                    ...original,
                    startDateTime: toIsoDate(offerWindow.startDateTime),
                    endDateTime: toIsoDate(offerWindow.endDateTime),
                });
        });

        if(contentGroup)
            await upsertContentGroupOfferWindows.mutateAsync({
                content:{
                    ...content,
                    offerWindows,
                },
                contentGroup: contentGroup,
            });
        else
            await upsertContentOfferWindows.mutateAsync({ ...content, offerWindows });
    };

    const defaultOfferWindow = contentState?.offerWindows
        ?.find(offerWindow => offerWindow.tenantId === "DEFAULT");

    return (
        <>
            {
                contentState ?
                <Modal modalTitle = {"Add/Edit Offer Windows"} modalId = {`${modalId}-modal`} openModal = {openModal} onClose = {onClose}>
                    <EditForm
                        modalId = {`${modalId}-form`}
                        initialValues = {{ offerWindows: contentOfferWindows } as OfferWindowFormValues}
                        onSubmit = {submit}
                    >
                        <OfferWindowUpsertForm
                            ingestorProperties={ingestorProperties}
                            newOfferWindowInitialValues = {defaultOfferWindow
                                ? {
                                    startDateTime: defaultOfferWindow.startDateTime,
                                    endDateTime: defaultOfferWindow.endDateTime,
                                }
                                : {}}
                        />
                    </EditForm>
                </Modal> : <></>
            }
        </>
    );
};

export default OfferWindowsForm;